// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/* eslint-disable spire/fenced-imports */
import Tippy, { TippyProps } from "@tippyjs/react";

import { color } from "@flip/XDL/Utilities";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import { IconMemo, IconPresentationProps, IconWrapper } from "@insite/mobius/Icon";
import { StyledClickable } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import applyPropBuilder from "@insite/mobius/utilities/applyPropBuilder";
import get from "@insite/mobius/utilities/get";
import InjectableCss, { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import MobiusStyledComponentProps from "@insite/mobius/utilities/MobiusStyledComponentProps";
import omitMultiple from "@insite/mobius/utilities/omitMultiple";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React, { CSSProperties } from "react";
import styled, { withTheme } from "styled-components";

export interface TooltipPresentationProps {
    /** CSS strings or styled-components functions to be injected into nested components. These will override the theme defaults.
     * @themable */
    cssOverrides?: {
        container?: CSSProperties;
        tooltipClickable?: StyledProp<TooltipProps>;
        tooltipBody?: StyledProp<TooltipProps>;
        tooltipContainer?: StyledProp<TooltipProps>;
        tooltipWrapper?: StyledProp<TooltipProps>;
    };
    /** Props to be passed into the trigger Icon component if no triggerComponent is provided.
     * @themable */
    iconProps?: IconPresentationProps;
    /** Props that will be passed to the typography component rendered in the tooltip.
     * @themable */
    typographyProps?: TypographyPresentationProps;
}

export type TooltipComponentProps = MobiusStyledComponentProps<
    "span",
    {
        /** Component to be used as the trigger to optionally replace the default trigger icon. */
        triggerComponent?: React.ReactElement;
        /** The text that will appear in the tooltip */
        text?: string | React.ReactElement | React.ReactNode | React.ReactNode[];
        /** Function called on tooltip open */
        onOpen?: React.EventHandler<React.SyntheticEvent>;
        /** Function called on tooltip close */
        onClose?: React.EventHandler<React.SyntheticEvent>;
        /** Text to be used to describe the tooltip trigger. Used for screenreaders. Defaults to "more info" */
        triggerAltText?: string;
    }
>;

type TooltipProps = TooltipComponentProps & TooltipPresentationProps & TippyProps;

const TooltipContainer = styled.div<InjectableCss<any>>`
    &.CMS-Mode {
        border: 0.8px solid #dfe3e6;
        border-radius: 4px;
        background-color: #ffffff;
        box-shadow: 0 2px 4px 0 rgba(109, 120, 120, 0.25);
        padding: 15px;
    }
    ${injectCss}
`;

const TooltipBody = styled.div<InjectableCss<any>>`
    position: relative;
    z-index: 2;
    color: ${color("text.main")};
    ${injectCss}
`;

const TooltipTriggerer = styled.span.attrs({ tabIndex: 0 })<InjectableCss<any>>`
    display: inline-block;
    vertical-align: middle;
    & > ${StyledClickable} {
        display: inline-block;
    }
    &:focus {
        outline: none;
    }

    &&& ${IconWrapper} {
        margin-right: 0;
    }

    ${injectCss}
`;

/**
 * Component that provides supplementatal information on click or enter.
 */

const Tooltip: React.FC<TooltipProps> = props => {
    // XNG-Changes: To fix tooltip style in CMS field tooltip
    const isEditingInShell = useShellContext().isEditing;

    const { text, triggerComponent, triggerAltText, iconProps, onOpen, onClose, ...otherProps } = props;
    if (
        !text ||
        (typeof text === "string" && text.length === 0) ||
        React.isValidElement(text) ||
        (Array.isArray(text) && text.length === 0)
    ) {
        return triggerComponent || null;
    }

    const { spreadProps } = applyPropBuilder(props, { component: "tooltip" });
    const cssOverrides = spreadProps("cssOverrides");
    let trigger: {} | null | undefined;

    if (triggerComponent) {
        trigger = triggerComponent;
    } else {
        trigger = <IconMemo {...spreadProps("iconProps")} />;
    }

    const toolTipComponent = (
        <Typography as="p" {...spreadProps("typographyProps")}>
            {text}
        </Typography>
    );

    return (
        <span className="hide-on-print tooltip-container" style={get(cssOverrides, "container")}>
            <Tippy
                onShow={instance => {
                    onOpen?.({ target: instance.reference, instance } as any);
                }}
                onHide={instance => {
                    onClose?.({ target: instance.reference, instance } as any);
                }}
                render={attrs => (
                    <TooltipContainer
                        css={get(cssOverrides, "tooltipContainer")}
                        {...attrs}
                        className={`${isEditingInShell ? "CMS-Mode" : ""}`}
                    >
                        <TooltipBody css={get(cssOverrides, "tooltipBody")}>{toolTipComponent}</TooltipBody>
                        <span data-popper-arrow="" className="tippy-arrow" />
                    </TooltipContainer>
                )}
                offset={({ placement }) => {
                    if (placement === "bottom-start" || placement === "top-start") {
                        return [-20, 20];
                    }

                    return [20, 20];
                }}
                {...omitMultiple(otherProps, ["cssOverrides", "typographyProps"])}
            >
                <TooltipTriggerer css={get(cssOverrides, "tooltipClickable")} role="link">
                    {trigger}
                    <VisuallyHidden>{triggerAltText || props.theme!.translate("more info")}</VisuallyHidden>
                </TooltipTriggerer>
            </Tippy>
        </span>
    );
};

Tooltip.defaultProps = {
    trigger: "mouseenter focus",
    placement: "bottom",
    interactive: true,
    animation: false,
};

/** @component */
export default withTheme(Tooltip);
